/**
 * Product Edit Requests Management - Minimal RTL Design
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import InboxIcon from '@mui/icons-material/Inbox';
import FilterListIcon from '@mui/icons-material/FilterList';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryIcon from '@mui/icons-material/History';
import axios from 'axios';
import { API_BASE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import MyShiftStatusBanner from '../components/common/MyShiftStatusBanner';
import { ChangesList, ChangeItem } from '../components/product/EditRequestDiffComponents';
import { EditHistoryTimeline, EditRequestSummary } from '../components/product/EditHistoryTimeline';

import './ProductEditRequestsManagement.css';
import AssigneeCell from '../components/common/AssigneeCell';

// Status enum
export enum ProductEditRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED'
}

// Interface
interface IProductEditRequest {
  _id: string;
  productId: { _id: string; name: string; thumbnail?: string };
  storeId: { _id: string; name: string };
  originalData: any;
  editData: any;
  status: ProductEditRequestStatus;
  approvedBy?: { _id: string; name: string };
  approvedAt?: Date;
  approvalNote?: string;
  rejectedBy?: { _id: string; name: string };
  rejectedAt?: Date;
  rejectionReason?: string;
  createdBy: { _id: string; name: string };
  createdAt: Date;
  updatedAt: Date;
}

const tabs = [
  { label: 'همه درخواست‌ها', value: undefined, color: 'primary', icon: <FilterListIcon /> },
  { label: 'در انتظار بررسی', value: ProductEditRequestStatus.PENDING, color: 'warning', icon: <PendingActionsIcon /> },
  { label: 'تایید شده', value: ProductEditRequestStatus.APPROVED, color: 'success', icon: <CheckCircleIcon /> },
  { label: 'رد شده', value: ProductEditRequestStatus.REJECTED, color: 'error', icon: <CancelIcon /> },
];

const fieldTranslations: Record<string, string> = {
  name: 'نام محصول', description: 'توضیحات', price: 'قیمت', discountPrice: 'قیمت با تخفیف', costPrice: 'قیمت خرید',
  stock: 'موجودی', sku: 'کد محصول', thumbnail: 'تصویر شاخص', images: 'تصاویر',
  tags: 'برچسب‌ها', status: 'وضعیت', accountTypeId: 'نوع حساب', fields: 'فیلدهای تخصصی',
  categoryId: 'دسته‌بندی', category: 'دسته‌بندی', brand: 'برند', brandId: 'برند',
  weight: 'وزن', dimensions: 'ابعاد', features: 'ویژگی‌ها', colors: 'رنگ‌ها', sizes: 'سایزها'
};

const ProductEditRequestsManagement: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [editRequests, setEditRequests] = useState<IProductEditRequest[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<IProductEditRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState<string | null>(null);
  const [changes, setChanges] = useState<ChangeItem[]>([]);
  const [history, setHistory] = useState<EditRequestSummary[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Tabs scroll
  const tabsRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      setScrollState({
        left: scrollLeft > 5,
        right: scrollLeft < scrollWidth - clientWidth - 5
      });
    }
  }, []);

  useEffect(() => {
    updateScrollState();
    const tabsEl = tabsRef.current;
    if (tabsEl) {
      tabsEl.addEventListener('scroll', updateScrollState);
      window.addEventListener('resize', updateScrollState);
    }
    return () => {
      if (tabsEl) tabsEl.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const fetchEditRequests = useCallback(async (pageNum: number = 1, status?: ProductEditRequestStatus) => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/admin/product-edit-requests?page=${pageNum}&limit=${rowsPerPage}`;
      if (status) url += `&status=${status}`;

      const token = localStorage.getItem('accessToken');
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data.data;
      if (Array.isArray(data)) {
        setEditRequests(data);
        const meta = response.data.meta || {};
        setTotalPages(meta.totalPages || 1);
        setTotalCount(meta.totalCount ?? meta.totalItems ?? data.length);
      } else if (data?.docs) {
        setEditRequests(data.docs);
        setTotalPages(data.totalPages);
        setTotalCount(data.totalDocs || data.docs.length);
      } else {
        setEditRequests([]);
        setTotalPages(1);
        setTotalCount(0);
      }
      setPage(pageNum);
    } catch (error: any) {
      console.error('Error fetching edit requests:', error?.response?.status, error?.response?.data, error);
      const status = error?.response?.status;
      const serverMsg = error?.response?.data?.message;
      const msg = status === 403
        ? 'دسترسی به این بخش ندارید (admin_product_view). با مدیر ارشد تماس بگیرید.'
        : status === 401
          ? 'نشست منقضی شده — لطفاً دوباره وارد شوید.'
          : serverMsg || `خطا در دریافت درخواست‌های ویرایش${status ? ` (${status})` : ''}`;
      enqueueSnackbar(msg, { variant: 'error' });
      setEditRequests([]);
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) fetchEditRequests(1, tabs[tabValue].value);
  }, [isAuthenticated, authLoading, tabValue, fetchEditRequests]);

  const fetchEditRequestDetails = async (requestId: string) => {
    try {
      setDetailsLoading(true);
      setChanges([]);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/product-edit-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const payload = response?.data?.data || {};
      if (payload.editRequest) {
        setSelectedRequest(prev => ({ ...(prev as any), ...payload.editRequest }));
      }

      // ✅ Backend already computes a rich diff (with AccountType field labels + types).
      // Just trust it and pass to ChangesList — no frontend extraction.
      const backendChanges: ChangeItem[] = Array.isArray(payload.changes) ? payload.changes : [];
      setChanges(backendChanges.filter(c => c && c.hasChanged !== false));

      // Fetch history in parallel (separate endpoint — admin-only product edit-requests by product)
      const productId = payload.editRequest?.productId?._id || payload.editRequest?.productId;
      if (productId) fetchProductEditHistory(productId);
    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchProductEditHistory = async (productId: string) => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/admin/product-edit-requests?productId=${productId}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response?.data?.data;
      const docs: EditRequestSummary[] = Array.isArray(data) ? data : data?.docs || [];
      setHistory(docs);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (detailsOpen && selectedRequest?._id) {
      fetchEditRequestDetails(selectedRequest._id);
    } else if (!detailsOpen) {
      setHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsOpen]);

  const formatDate = (dateString: Date | undefined): string => {
    if (!dateString) return 'نامشخص';
    try {
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      }).format(new Date(dateString));
    } catch { return 'نامعتبر'; }
  };

  const getStatusLabel = (status: ProductEditRequestStatus): string => {
    switch (status) {
      case ProductEditRequestStatus.PENDING: return 'در انتظار بررسی';
      case ProductEditRequestStatus.APPROVED: return 'تایید شده';
      case ProductEditRequestStatus.REJECTED: return 'رد شده';
      case ProductEditRequestStatus.CANCELLED: return 'لغو شده';
      default: return 'نامشخص';
    }
  };

  // Dialog handlers
  const openDetails = (request: IProductEditRequest) => {
    setSelectedRequest(request);
    setDetailsOpen(true);
  };

  const openApproveDialog = (request: IProductEditRequest) => {
    setSelectedRequest(request);
    setApprovalNote('');
    setOperationSuccess(null);
    setApproveOpen(true);
  };

  const openRejectDialog = (request: IProductEditRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setOperationSuccess(null);
    setRejectOpen(true);
  };

  const closeAll = () => {
    setDetailsOpen(false);
    setApproveOpen(false);
    setRejectOpen(false);
    setSelectedRequest(null);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setIsApproving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/product-edit-requests/${selectedRequest._id}/approve`,
        { approvalNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOperationSuccess('درخواست ویرایش با موفقیت تایید شد');
      enqueueSnackbar('درخواست ویرایش با موفقیت تایید شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchEditRequests(page, tabs[tabValue].value); }, 1000);
    } catch (error) {
      console.error('Error approving:', error);
      enqueueSnackbar('خطا در تایید درخواست ویرایش', { variant: 'error' });
    } finally { setIsApproving(false); }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      enqueueSnackbar('دلیل رد الزامی است', { variant: 'warning' });
      return;
    }
    setIsRejecting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/product-edit-requests/${selectedRequest._id}/reject`,
        { rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOperationSuccess('درخواست ویرایش با موفقیت رد شد');
      enqueueSnackbar('درخواست ویرایش با موفقیت رد شد', { variant: 'success' });
      setTimeout(() => { closeAll(); fetchEditRequests(page, tabs[tabValue].value); }, 1000);
    } catch (error) {
      console.error('Error rejecting:', error);
      enqueueSnackbar('خطا در رد درخواست ویرایش', { variant: 'error' });
    } finally { setIsRejecting(false); }
  };

  const handleTabChange = (index: number) => {
    setTabValue(index);
    setPage(1);
  };

  const renderValue = (value: any, key?: string): React.ReactNode => {
    if (value === null || value === undefined) return <span className="value-empty">خالی</span>;

    // Images
    if (key === 'images' || key === 'thumbnail' || (typeof value === 'string' && (value.startsWith('http') || value.startsWith('data:image')))) {
      if (Array.isArray(value)) {
        return (
          <div className="value-images">
            {value.slice(0, 4).map((img, i) => <img key={i} src={img} alt="" className="value-image" />)}
            {value.length > 4 && <span style={{ fontSize: 12, color: '#666' }}>+{value.length - 4}</span>}
          </div>
        );
      }
      return <img src={value} alt="" className="value-image" />;
    }

    // Boolean
    if (typeof value === 'boolean') {
      return <span className={`value-chip ${value ? 'yes' : 'no'}`}>{value ? 'بله' : 'خیر'}</span>;
    }

    // Array
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="value-empty">لیست خالی</span>;
      return (
        <div className="value-list">
          {value.slice(0, 5).map((item, i) => (
            <div key={i} className="value-list-item">
              {typeof item === 'object' ? JSON.stringify(item).substring(0, 50) : String(item)}
            </div>
          ))}
          {value.length > 5 && <div className="value-list-item" style={{ fontStyle: 'italic' }}>و {value.length - 5} مورد دیگر...</div>}
        </div>
      );
    }

    // Object
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return <span className="value-empty">آبجکت خالی</span>;
      return (
        <div className="value-list">
          {keys.slice(0, 5).map(k => (
            <div key={k} className="value-list-item">
              <strong>{k}:</strong> {typeof value[k] === 'object' ? JSON.stringify(value[k]).substring(0, 30) : String(value[k])}
            </div>
          ))}
          {keys.length > 5 && <div className="value-list-item" style={{ fontStyle: 'italic' }}>و {keys.length - 5} فیلد دیگر...</div>}
        </div>
      );
    }

    // Number (price)
    if ((key?.toLowerCase().includes('price') || key?.includes('قیمت')) && typeof value === 'number') {
      return <span>{new Intl.NumberFormat('fa-IR').format(value)} تومان</span>;
    }

    // String
    if (typeof value === 'string' && value.length > 100) {
      return <span title={value}>{value.substring(0, 100)}...</span>;
    }

    return <span>{String(value)}</span>;
  };

  return (
    <div className="edit-requests-page">
      <MyShiftStatusBanner category="product_edit" />
      {/* Header */}
      <header className="edit-requests-header">
        <h1><EditIcon /> مدیریت درخواست‌های ویرایش محصولات</h1>
        <div className="header-actions">
          {loading && <CircularProgress size={20} />}
          <button className="header-btn refresh" onClick={() => fetchEditRequests(page, tabs[tabValue].value)}>
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className={`edit-requests-tabs-wrapper ${scrollState.left ? 'scroll-left' : ''} ${scrollState.right ? 'scroll-right' : ''}`}>
        <div className="edit-requests-tabs" ref={tabsRef}>
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`edit-tab ${tabValue === index ? `active ${tab.color}` : ''}`}
              onClick={() => handleTabChange(index)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="edit-requests-table-wrapper">
        <table className="edit-requests-table">
          <thead>
            <tr>
              <th>محصول</th>
              <th>فروشگاه</th>
              <th>وضعیت</th>
              <th>مسئول</th>
              <th>تاریخ ایجاد</th>
              <th>ایجاد کننده</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {(authLoading || loading) && !editRequests.length ? (
              <tr>
                <td colSpan={7}>
                  <div className="loading-overlay"><CircularProgress size={30} /></div>
                </td>
              </tr>
            ) : editRequests.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">
                    <InboxIcon />
                    <p>هیچ درخواست ویرایشی یافت نشد</p>
                    <button className="header-btn" style={{ marginTop: 12 }} onClick={() => fetchEditRequests(page, tabs[tabValue].value)}>
                      <RefreshIcon fontSize="small" /> بررسی مجدد
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              editRequests.map(request => (
                <tr key={request._id} onClick={() => openDetails(request)}>
                  <td>
                    <div className="product-cell">
                      {request.productId?.thumbnail ? (
                        <img src={request.productId.thumbnail} alt={request.productId.name} className="product-thumb" />
                      ) : (
                        <div className="product-thumb-placeholder">
                          {request.productId?.name?.charAt(0)}
                        </div>
                      )}
                      <span className="product-name">{request.productId?.name}</span>
                    </div>
                  </td>
                  <td><span className="store-name">{request.storeId?.name || 'نامشخص'}</span></td>
                  <td>
                    <span className={`status-badge ${request.status}`}>
                      {getStatusLabel(request.status)}
                    </span>
                  </td>
                  <td><AssigneeCell value={(request as any).assignedTo} /></td>
                  <td>{formatDate(request.createdAt)}</td>
                  <td>{request.createdBy?.name || 'نامشخص'}</td>
                  <td>
                    <div className="actions-cell" onClick={e => e.stopPropagation()}>
                      <button className="action-btn" onClick={() => openDetails(request)} title="جزئیات">
                        <VisibilityIcon fontSize="small" />
                      </button>
                      {request.status === ProductEditRequestStatus.PENDING && (
                        <>
                          <button className="action-btn approve" onClick={() => openApproveDialog(request)} title="تایید">
                            <CheckCircleIcon fontSize="small" />
                          </button>
                          <button className="action-btn reject" onClick={() => openRejectDialog(request)} title="رد">
                            <CancelIcon fontSize="small" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="edit-requests-pagination">
            <span className="pagination-info">
              نمایش {(page - 1) * rowsPerPage + 1} تا {Math.min(page * rowsPerPage, totalCount)} از {totalCount}
            </span>
            <div className="pagination-controls">
              <select
                className="pagination-select"
                value={rowsPerPage}
                onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </select>
              <div className="pagination-btns">
                <button className="pagination-btn" disabled={page <= 1} onClick={() => { setPage(p => p - 1); fetchEditRequests(page - 1, tabs[tabValue].value); }}>
                  قبلی
                </button>
                <button className="pagination-btn" disabled={page >= totalPages} onClick={() => { setPage(p => p + 1); fetchEditRequests(page + 1, tabs[tabValue].value); }}>
                  بعدی
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="edit-requests-cards">
        {editRequests.map(request => (
          <div key={request._id} className="edit-request-card" onClick={() => openDetails(request)}>
            <div className="edit-request-card-header">
              {request.productId?.thumbnail ? (
                <img src={request.productId.thumbnail} alt={request.productId.name} className="edit-request-card-thumb" />
              ) : (
                <div className="edit-request-card-thumb-placeholder">{request.productId?.name?.charAt(0)}</div>
              )}
              <div className="edit-request-card-info">
                <div className="edit-request-card-title">{request.productId?.name}</div>
                <div className="edit-request-card-store">{request.storeId?.name}</div>
              </div>
              <span className={`status-badge ${request.status}`}>{getStatusLabel(request.status)}</span>
            </div>
            <div className="edit-request-card-row">
              <span className="edit-request-card-label">تاریخ:</span>
              <span className="edit-request-card-value">{formatDate(request.createdAt)}</span>
            </div>
            {request.status === ProductEditRequestStatus.PENDING && (
              <div className="edit-request-card-actions" onClick={e => e.stopPropagation()}>
                <button className="action-btn approve" onClick={() => openApproveDialog(request)}><CheckCircleIcon fontSize="small" /></button>
                <button className="action-btn reject" onClick={() => openRejectDialog(request)}><CancelIcon fontSize="small" /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Details Dialog (rewritten 2026-04-29 with MUI to fix layout) */}
      <Dialog
        open={detailsOpen && !!selectedRequest}
        onClose={closeAll}
        maxWidth="lg"
        fullWidth
        dir="rtl"
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}
      >
        {selectedRequest && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                pb: 1.5,
                borderBottom: '1px solid #eee',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon fontSize="small" />
                <Box>
                  <Typography component="div" sx={{ fontWeight: 700, fontSize: 17 }}>
                    جزئیات درخواست ویرایش
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#5f6368' }}>
                    کد: {selectedRequest._id.slice(-6)}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={closeAll} size="small" sx={{ mt: -0.5 }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2.5, pb: 1 }}>
              {/* Product header card */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  border: '1px solid #eee',
                  borderRadius: 2,
                  mb: 2.5,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                {selectedRequest.productId?.thumbnail ? (
                  <Box
                    component="img"
                    src={selectedRequest.productId.thumbnail}
                    alt={selectedRequest.productId.name}
                    sx={{ width: 72, height: 72, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1.5,
                      background: '#f1f3f4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28,
                      fontWeight: 700,
                      color: '#5f6368',
                      flexShrink: 0,
                    }}
                  >
                    {selectedRequest.productId?.name?.charAt(0) || '?'}
                  </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap" useFlexGap>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
                      {selectedRequest.productId?.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={getStatusLabel(selectedRequest.status)}
                      sx={{
                        background:
                          selectedRequest.status === 'PENDING'
                            ? '#fef7e0'
                            : selectedRequest.status === 'APPROVED'
                            ? '#e6f4ea'
                            : selectedRequest.status === 'REJECTED'
                            ? '#fce8e6'
                            : '#f1f3f4',
                        color:
                          selectedRequest.status === 'PENDING'
                            ? '#a47a00'
                            : selectedRequest.status === 'APPROVED'
                            ? '#137333'
                            : selectedRequest.status === 'REJECTED'
                            ? '#a50e0e'
                            : '#5f6368',
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', lineHeight: 1.8 }}>
                    <strong>فروشگاه:</strong> {selectedRequest.storeId?.name || 'نامشخص'}
                    {' • '}
                    <strong>درخواست‌کننده:</strong> {selectedRequest.createdBy?.name || 'نامشخص'}
                    {' • '}
                    <strong>تاریخ:</strong> {formatDate(selectedRequest.createdAt)}
                  </Typography>
                </Box>

                {selectedRequest.status === ProductEditRequestStatus.PENDING && (
                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => { closeAll(); openApproveDialog(selectedRequest); }}
                    >
                      تایید
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() => { closeAll(); openRejectDialog(selectedRequest); }}
                    >
                      رد
                    </Button>
                  </Stack>
                )}
              </Box>

              {/* Changes section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <EditIcon fontSize="small" sx={{ color: '#5f6368' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  تغییرات درخواست شده {changes.length > 0 && `(${changes.length} فیلد)`}
                </Typography>
              </Box>
              {detailsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <ChangesList changes={changes} />
              )}

              {/* Status info banners */}
              {selectedRequest.status === ProductEditRequestStatus.APPROVED && selectedRequest.approvalNote && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                    یادداشت تایید — توسط {selectedRequest.approvedBy?.name || 'ادمین'} در {formatDate(selectedRequest.approvedAt)}:
                  </Typography>
                  {selectedRequest.approvalNote}
                </Alert>
              )}
              {selectedRequest.status === ProductEditRequestStatus.REJECTED && selectedRequest.rejectionReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                    دلیل رد — توسط {selectedRequest.rejectedBy?.name || 'ادمین'} در {formatDate(selectedRequest.rejectedAt)}:
                  </Typography>
                  {selectedRequest.rejectionReason}
                </Alert>
              )}

              <Divider sx={{ my: 2.5 }} />

              {/* History timeline */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <HistoryIcon fontSize="small" sx={{ color: '#5f6368' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  تاریخچهٔ ویرایش‌های این محصول {history.length > 0 && `(${history.length})`}
                </Typography>
              </Box>
              {historyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <EditHistoryTimeline history={history} />
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', gap: 1 }}>
              <Button onClick={closeAll}>بستن</Button>
              <Box sx={{ flex: 1 }} />
              {selectedRequest.status === ProductEditRequestStatus.PENDING && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => { closeAll(); openRejectDialog(selectedRequest); }}
                  >
                    رد درخواست
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => { closeAll(); openApproveDialog(selectedRequest); }}
                  >
                    تایید درخواست
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Approve Dialog */}
      {approveOpen && selectedRequest && (
        <div className="dialog-overlay" onClick={closeAll}>
          <div className="dialog small" onClick={e => e.stopPropagation()}>
            <div className="dialog-header success">
              <h2><CheckCircleIcon /> تایید درخواست ویرایش</h2>
              <button className="dialog-close" onClick={closeAll}><CloseIcon /></button>
            </div>
            <div className="dialog-content">
              {operationSuccess ? (
                <div className="success-message">
                  <CheckCircleIcon />
                  <p>{operationSuccess}</p>
                </div>
              ) : (
                <>
                  <p className="confirm-text">
                    آیا از تایید درخواست ویرایش محصول <strong>{selectedRequest.productId?.name}</strong> اطمینان دارید؟
                  </p>
                  <div className="confirm-box success">
                    <CheckCircleIcon fontSize="small" />
                    <p className="confirm-hint" style={{ margin: 0 }}>
                      با تایید این درخواست، تغییرات بر روی محصول اعمال خواهد شد.
                    </p>
                  </div>
                  <textarea
                    className="form-textarea"
                    value={approvalNote}
                    onChange={e => setApprovalNote(e.target.value)}
                    placeholder="یادداشت (اختیاری)..."
                    style={{ marginTop: 16 }}
                  />
                  <p className="form-hint">این یادداشت برای فروشنده قابل مشاهده خواهد بود</p>
                </>
              )}
            </div>
            <div className="dialog-actions">
              <button className="dialog-btn cancel" onClick={closeAll} disabled={isApproving}>انصراف</button>
              <button className="dialog-btn success" onClick={handleApprove} disabled={isApproving || !!operationSuccess}>
                {isApproving ? <CircularProgress size={18} color="inherit" /> : <><CheckCircleIcon fontSize="small" /> تایید</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectOpen && selectedRequest && (
        <div className="dialog-overlay" onClick={closeAll}>
          <div className="dialog small" onClick={e => e.stopPropagation()}>
            <div className="dialog-header error">
              <h2><CancelIcon /> رد درخواست ویرایش</h2>
              <button className="dialog-close" onClick={closeAll}><CloseIcon /></button>
            </div>
            <div className="dialog-content">
              {operationSuccess ? (
                <div className="success-message">
                  <CheckCircleIcon />
                  <p>{operationSuccess}</p>
                </div>
              ) : (
                <>
                  <p className="confirm-text">
                    دلیل رد درخواست ویرایش محصول <strong>{selectedRequest.productId?.name}</strong>:
                  </p>
                  <div className="confirm-box error">
                    <InfoIcon fontSize="small" />
                    <p className="confirm-hint" style={{ margin: 0 }}>
                      لطفاً دلیل رد را به صورت واضح بنویسید تا فروشنده بتواند مشکل را برطرف کند.
                    </p>
                  </div>
                  <textarea
                    className={`form-textarea ${!rejectionReason.trim() ? 'error' : ''}`}
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="دلیل رد را وارد کنید..."
                    style={{ marginTop: 16 }}
                  />
                  {!rejectionReason.trim() && <p className="form-error">وارد کردن دلیل الزامی است</p>}
                  <p className="form-hint">این دلیل برای فروشنده قابل مشاهده خواهد بود</p>
                </>
              )}
            </div>
            <div className="dialog-actions">
              <button className="dialog-btn cancel" onClick={closeAll} disabled={isRejecting}>انصراف</button>
              <button
                className="dialog-btn error"
                onClick={handleReject}
                disabled={isRejecting || !rejectionReason.trim() || !!operationSuccess}
              >
                {isRejecting ? <CircularProgress size={18} color="inherit" /> : <><CancelIcon fontSize="small" /> رد</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductEditRequestsManagement;
